import React, { useState } from "react";
import { Mail, MapPin, Phone, Loader2, Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SectionHeader from "@/components/shared/SectionHeader";
import { postContact } from "@/lib/api";
import { toast } from "sonner";
import { EISG } from "@/constants/testIds";

const EISGContact = () => {
  const [form, setForm] = useState({ full_name: "", email: "", organisation: "", subject: "", message: "", source: "eisg" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Veuillez renseigner nom, email et message.");
      return;
    }
    setLoading(true);
    try {
      await postContact(form);
      toast.success("Message envoyé. Nous reviendrons vers vous sous 48 heures.");
      setForm({ full_name: "", email: "", organisation: "", subject: "", message: "", source: "eisg" });
    } catch (err) {
      toast.error("Erreur. Écrivez à contact@bridgedatum.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="py-28 lg:py-36 bg-[#FAFAFA] border-b border-[#0A192F]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader
            number="— Contact"
            eyebrow="Parlez à EISG"
            title="Inscriptions, proposition institutionnelle, in-house sur mesure."
            subtitle="Notre équipe vous répond sous 48 heures."
            body="Pour toute demande exécutive : contact@bridgedatum.com — WhatsApp +27 83 627 4861."
            titleFont="eisg"
          />
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline text-[#0A192F]/60 mb-4">— Lignes directes</div>
            <h3 className="font-eisg text-3xl text-[#0A192F] tracking-tight">Comment nous joindre.</h3>
            <ul className="mt-10 space-y-6 text-[#0A192F]/85">
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#0A192F]/20 flex items-center justify-center"><Mail size={16} /></div>
                <div>
                  <div className="overline text-[#0A192F]/60">Inscriptions & informations</div>
                  <a className="font-bd text-lg link-underline" href="mailto:contact@bridgedatum.com">contact@bridgedatum.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#0A192F]/20 flex items-center justify-center"><MessageCircle size={16} /></div>
                <div>
                  <div className="overline text-[#0A192F]/60">WhatsApp South Africa</div>
                  <a className="font-bd text-lg link-underline" href="https://wa.me/27836274861">+27 83 627 4861</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#0A192F]/20 flex items-center justify-center"><Phone size={16} /></div>
                <div>
                  <div className="overline text-[#0A192F]/60">Site web</div>
                  <a className="font-bd text-lg link-underline" href="https://www.bridgedatum.com">www.bridgedatum.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#0A192F]/20 flex items-center justify-center"><MapPin size={16} /></div>
                <div>
                  <div className="overline text-[#0A192F]/60">Base</div>
                  <p className="font-bd text-lg">Centurion, South Africa</p>
                  <p className="text-sm text-[#0A192F]/70">Locations : Johannesburg · Cape Town</p>
                </div>
              </li>
            </ul>
          </div>

          <form onSubmit={submit} data-testid={EISG.contactForm} className="lg:col-span-7 bg-[#FAFAFA] p-8 lg:p-12 border border-[#0A192F]/10">
            <div className="overline text-[#F58220] mb-2">Message</div>
            <h3 className="font-eisg text-2xl text-[#0A192F] tracking-tight mb-8">Écrivez-nous</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Nom et prénom *</Label>
                <Input value={form.full_name} onChange={upd("full_name")} required className="mt-1 rounded-none border-[#0A192F]/20 bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Email *</Label>
                <Input type="email" value={form.email} onChange={upd("email")} required className="mt-1 rounded-none border-[#0A192F]/20 bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Organisation</Label>
                <Input value={form.organisation} onChange={upd("organisation")} className="mt-1 rounded-none border-[#0A192F]/20 bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Sujet</Label>
                <Input value={form.subject} onChange={upd("subject")} className="mt-1 rounded-none border-[#0A192F]/20 bg-white" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Message *</Label>
                <Textarea rows={5} value={form.message} onChange={upd("message")} required className="mt-1 rounded-none border-[#0A192F]/20 bg-white" />
              </div>
            </div>
            <Button data-testid={EISG.contactSubmit} type="submit" disabled={loading} className="mt-8 bg-[#0A192F] hover:bg-[#001529] text-white rounded-none h-12 px-8 font-semibold tracking-wider text-xs uppercase">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={14} className="mr-2" />}
              Envoyer
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EISGContact;
